import {loggerFactory} from "@cm/browser-log"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {
    cachedVariationOracle,
    computeLayerPlan,
    LayerPlan,
    ConfigInfo,
    countVariationsFast,
    gatherAllVariations as gatherAllVariationsExhaustive,
    generateVariationsFast,
    matchesVariationFilter,
    Parameters,
    SceneNodes,
    UnsupportedVariationStructureError,
    VariationCounter,
    VariationFilter,
    VariationGroup,
    VariationOracle,
    VariationPart,
    VariationScope,
    VariationStructure,
} from "@cm/template-nodes"
import {sortedJSONStringify} from "@cm/utils/sorted-json-stringify"
import {Observable} from "rxjs"

const allVariationsLogger = loggerFactory.getLogger("templates")

export type ConfigurationInfo = {
    name: string
    parameters: Parameters
    hash: string
    scene: SceneNodes.SceneNode[]
}

export type VariationWithSceneSettings = Omit<ConfigurationInfo, "scene"> & {
    parts: VariationPart[]
    renderSettings: SceneNodes.RenderSettings | undefined
    postProcessingSettings: SceneNodes.RenderPostProcessingSettings | undefined
}

function withSceneSettings(
    {name, parts, parameters, hash}: Omit<ConfigurationInfo, "scene"> & {parts: VariationPart[]},
    scene: SceneNodes.SceneNode[],
): VariationWithSceneSettings {
    return {
        name,
        parts,
        parameters,
        hash,
        renderSettings: scene.find(SceneNodes.RenderSettings.is),
        postProcessingSettings: scene.find(SceneNodes.RenderPostProcessingSettings.is),
    }
}

function sceneManagerOracle(localSceneManagerService: SceneManagerService): VariationOracle<SceneNodes.SceneNode[]> {
    return {
        evaluate: async (parameters) => {
            localSceneManagerService.$instanceParameters.set(new Parameters({parameters}))
            localSceneManagerService.compileTemplate()
            await localSceneManagerService.syncSolved(true)
            return {
                descriptors: localSceneManagerService.getDescriptorsForNode(localSceneManagerService.$templateInstance()),
                extra: localSceneManagerService.$scene(),
            }
        },
    }
}

export function gatherAllVariations(
    localSceneManagerService: SceneManagerService,
    scope: VariationScope,
    progress?: (progress: number) => void,
    configInfoSideEffect?: (configInfo: ConfigInfo) => void,
) {
    return new Observable<ConfigurationInfo | "done">((observer) => {
        let isAborted = false

        gatherAllVariationsExhaustive(sceneManagerOracle(localSceneManagerService), scope, {
            onVariation: ({name, parameters, hash, extra}) => observer.next({name, parameters, hash, scene: extra}),
            onProgress: progress,
            onConfigInfo: configInfoSideEffect,
            isAborted: () => isAborted,
        }).then(
            () => {
                observer.next("done")
                observer.complete()
            },
            (error) => observer.error(error),
        )

        return () => {
            isAborted = true
        }
    })
}

// One per loaded template: structure, compile cache and counter are reused across filter changes.
export class VariationSession {
    private constructor(
        private readonly oracle: VariationOracle<SceneNodes.SceneNode[]>,
        private structure: VariationStructure | undefined,
        private readonly counter: VariationCounter | undefined,
    ) {}

    static async create(
        localSceneManagerService: SceneManagerService,
        onStatus?: (status: string) => void,
        onProgress?: (fraction: number) => void,
    ): Promise<VariationSession> {
        const oracle = cachedVariationOracle(sceneManagerOracle(localSceneManagerService))
        try {
            onStatus?.("Analyzing template…")
            const structure = await VariationStructure.analyze(localSceneManagerService.$templateGraph(), localSceneManagerService)
            const counter = new VariationCounter(oracle, structure)
            onStatus?.("Counting variations…")
            await counter.count([], undefined, onProgress)
            return new VariationSession(oracle, structure, counter)
        } catch (error) {
            if (!(error instanceof UnsupportedVariationStructureError)) throw error
            allVariationsLogger.info(`${error.message}; facets are unavailable`)
            return new VariationSession(oracle, undefined, undefined)
        }
    }

    // Only gates and the groups feeding render or post-processing nodes are compiled, so contexts that agree on those share one
    // cached compile.
    async postProcessingSettings(assignment: Readonly<Record<string, string>>) {
        const structure = this.structure
        const relevant = structure
            ? Object.fromEntries(Object.entries(assignment).filter(([key]) => structure.isGate(key) || structure.affectsSceneSettings(key)))
            : {...assignment}
        const {extra: scene} = await this.oracle.evaluate(relevant)
        return scene.find(SceneNodes.RenderPostProcessingSettings.is)
    }

    get supportsCounting() {
        return this.structure !== undefined
    }

    groups(): VariationGroup[] {
        return this.counter?.groups() ?? []
    }

    count(filter: VariationFilter, isAborted?: () => boolean): Promise<bigint | undefined> {
        return this.guard(() => this.counter?.count(filter, isAborted))
    }

    private readonly optionCountsByFilter = new Map<string, Map<string, Map<string, bigint>>>()

    async optionCounts(filter: VariationFilter, isAborted?: () => boolean) {
        const key = sortedJSONStringify(filter)
        const cached = this.optionCountsByFilter.get(key)
        if (cached) return cached
        const counts = await this.guard(() => this.counter?.optionCounts(filter, isAborted))
        if (counts) this.optionCountsByFilter.set(key, counts)
        return counts
    }

    private layerPlanResult?: Promise<LayerPlan | undefined>
    private layerPlanFraction = 0
    private readonly layerPlanListeners = new Set<(fraction: number) => void>()

    // Computed once per session and shared by every subscriber; it keeps running after all of them unsubscribe.
    layerPlan(onProgress?: (fraction: number) => void) {
        return new Observable<LayerPlan | undefined>((observer) => {
            if (onProgress) {
                this.layerPlanListeners.add(onProgress)
                onProgress(this.layerPlanFraction)
            }
            this.layerPlanResult ??= this.guard(() => {
                const structure = this.structure
                if (!structure) return undefined
                return computeLayerPlan(this.oracle, structure, {
                    sceneOf: (scene) => scene,
                    onProgress: (fraction) => {
                        this.layerPlanFraction = fraction
                        for (const listener of this.layerPlanListeners) listener(fraction)
                    },
                })
            })
            this.layerPlanResult.then(
                (plan) => {
                    observer.next(plan)
                    observer.complete()
                },
                (error) => observer.error(error),
            )
            return () => {
                if (onProgress) this.layerPlanListeners.delete(onProgress)
            }
        })
    }

    enumerate(
        filter: VariationFilter,
        progress?: (progress: number) => void,
        configInfoSideEffect?: (configInfo: ConfigInfo) => void,
        total?: (count: bigint) => void,
    ) {
        return new Observable<VariationWithSceneSettings | "done">((observer) => {
            let isAborted = false
            const emitted = new Set<string>()
            const emit = (variation: Omit<ConfigurationInfo, "scene"> & {parts: VariationPart[]}, scene: SceneNodes.SceneNode[]) => {
                if (emitted.has(variation.hash)) return
                emitted.add(variation.hash)
                observer.next(withSceneSettings(variation, scene))
            }

            const run = async () => {
                progress?.(0)
                const structure = this.structure
                try {
                    if (!structure) throw new UnsupportedVariationStructureError("structure analysis unavailable")
                    const count = await countVariationsFast(this.oracle, structure, {filter, isAborted: () => isAborted})
                    total?.(count)
                    await generateVariationsFast(this.oracle, structure, "local", {
                        filter,
                        keepSceneSettingsExact: true,
                        onVariation: (variation) => {
                            emit(variation, variation.extra)
                            if (count > 0n) progress?.(Math.min(100, (emitted.size / Number(count)) * 100))
                        },
                        onConfigInfo: configInfoSideEffect,
                        isAborted: () => isAborted,
                    })
                } catch (error) {
                    if (!(error instanceof UnsupportedVariationStructureError)) throw error
                    if (structure) {
                        allVariationsLogger.info(`${error.message}; falling back to exhaustive enumeration`)
                        this.structure = undefined
                    }
                    // Variations emitted before the fallback are skipped, so the result stays the exhaustive set.
                    await gatherAllVariationsExhaustive(this.oracle, "local", {
                        onVariation: (variation) => {
                            if (matchesVariationFilter(variation.parameters.parameters, filter)) emit(variation, variation.extra)
                        },
                        onProgress: progress,
                        onConfigInfo: configInfoSideEffect,
                        isAborted: () => isAborted,
                    })
                }
            }

            run().then(
                () => {
                    observer.next("done")
                    observer.complete()
                },
                (error) => {
                    if (!isAborted) observer.error(error)
                },
            )

            return () => {
                isAborted = true
            }
        })
    }

    private async guard<T>(run: () => Promise<T> | undefined): Promise<T | undefined> {
        try {
            return await run()
        } catch (error) {
            if (!(error instanceof UnsupportedVariationStructureError)) throw error
            allVariationsLogger.info(`${error.message}; facets are unavailable`)
            this.structure = undefined
            return undefined
        }
    }
}
